import { useWidthTier } from '@lib/hooks/sizing/element-size';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { debugStore } from '@stores/Debug';
import { useUIPreferences } from '@stores/UIPreferences';
import { observer } from 'mobx-react';
import { useSearchParams } from 'react-router-dom';
import { DebugMediaCardGrid } from './components/DebugMediaCardGrid/DebugMediaCardGrid';
import { LibraryFolderBar } from './components/Folders/LibraryFolderBar';
import { LibraryContentHeaderLeft } from './components/LibraryContentHeaderLeft/LibraryContentHeaderLeft';
import { LibraryContentHeaderRight } from './components/LibraryContentHeaderRight/LibraryContentHeaderRight';
import { LibraryContentMediaList } from './components/LibraryContentMediaList/LibraryContentMediaList';
import { LibraryVideoSelection } from './components/LibraryContentMediaList/LibraryVideoSelection';
import { LibraryContentMediaTable } from './components/LibraryContentMediaTable/LibraryContentMediaTable';
import { LibraryToolbar } from './components/LibraryToolbar/LibraryToolbar';
import { MobileLibraryHeader } from './components/Mobile/MobileLibraryHeader';
import { MobileMediaTableList } from './components/Mobile/MobileMediaTableList';
import { AddVideosModalProvider } from './context/AddVideosModalContext';
import { MediaTableProvider } from './context/MediaTableContext';
import css from './LibraryContent.module.scss';

/**
 * STAGE 1: below this much room for the header row (plus Atlas's cost), `.headerRight` abbreviates
 * — Atlas drops its word for its glyph.
 *
 * FIRST, before the verb labels, and the order is the whole point. "Atlas" names a control the
 * user can re-read in a tooltip, while "Chat with an Agent" and "Find moments" are the only
 * labelled copy of those verbs inside a library — they are teaching, not labelling.
 *
 * Derived from the fully-labelled row: 710 for the labelled left cluster (see `VERB_LABELS_BELOW`)
 * + the row's 24 gap + 52 padding = 786, rounded to 790, with the right cluster's own width added
 * by `ATLAS_LABELLED_COST`. Atlas is all that cluster holds: the view switch and Add media moved to
 * the search line (`LibraryToolbar`) with autoplay, Filters and Sort, which is why this was 1320
 * when they were all up here. Without Atlas the stage has nothing to abbreviate.
 */
const RIGHT_LABELS_BELOW = 790;

/**
 * STAGE 2: below this much room for the row, the library's two verb buttons also drop their labels
 * and become a `VerbRail`.
 *
 * Derived from the labelled pills' real need — ~150px of legible name (`.headerLeft`'s share, see
 * its `min-width`) + 18 transcript badge + 36 kebab + 385 pills (the agent pill 209 — measured
 * as "Work with an Agent" at 16px/500, ~10px wider than today's 15px/600 pill — + 8 + "Find
 * moments" 168, ~3px wide) + 32 gaps/margin ≈ 621, rounded to 710 for the ~90px of headroom an
 * older title-row threshold carried (a font fallback before Inter loads, a longer label, a wider
 * kebab).
 * Plus the row's 24 gap and 52 padding = 786, rounded to 790, and the ABBREVIATED right cluster's
 * width by `ATLAS_GLYPH_COST`. Stage 1 has already run by here, which is what makes the two stages
 * orderable: this one is measured against a cluster that has already given up its words.
 *
 * Both thresholds are far below the 1012px of row a 1280px window gives with the nav rail
 * expanded, one of the most common resting widths, so neither flickers there.
 */
const VERB_LABELS_BELOW = 790;

/**
 * What the Atlas button adds to the row: 94px labelled ("Atlas" at 14px/600 plus a glyph and 32px
 * of padding, measured) or a 40px circle abbreviated. It has no sibling, so no gap.
 *
 * Two figures because the two stages measure two different right clusters — stage 1 against the
 * labelled one, stage 2 against the abbreviated one.
 *
 * Added conditionally rather than baked into the constants because `AtlasButton` renders `null` most
 * of the time — and NOT rarely enough to ignore. `debugStore.showStoryAtlas` is true for team email
 * domains, for certain subscription products, and (fail-open) whenever billing failed to load, so
 * this is a real user-facing configuration, not just a debug one. Baking it in would abbreviate
 * early for everyone who doesn't have it; leaving it out would let the header scroll for everyone
 * who does.
 */
const ATLAS_LABELLED_COST = 96;
const ATLAS_GLYPH_COST = 40;

export const LibraryContent = observer(() => {
  const isMobile = useIsMobile();

  // Both stages from ONE measurement of the row — see `useWidthTier` for why they cannot each
  // measure their own cluster. Widest first, and each stage demands no more than the one above it
  // (886 → 830 with Atlas, 790 for both without), so the row only ever gets simpler as it narrows:
  // everything labelled → Atlas as a glyph → verbs as glyphs too → (last resort) scroll.
  const atlas = debugStore.showStoryAtlas;
  const [headerRef, headerStage] = useWidthTier<HTMLDivElement>([
    RIGHT_LABELS_BELOW + (atlas ? ATLAS_LABELLED_COST : 0),
    VERB_LABELS_BELOW + (atlas ? ATLAS_GLYPH_COST : 0),
  ]);
  const rightCompact = headerStage >= 1;
  const verbsCollapsed = headerStage >= 2;
  const uiPreferences = useUIPreferences();
  const [searchParams] = useSearchParams();
  const showDebugMediaCards = debugStore.showMediaCardsDebug && searchParams.has('debug');

  // Note: the omnifilter (folder._omniFilter) is view-independent — both grid and
  // table read it directly — so there's no filter migration needed when switching
  // views. The table's per-column filters live in folder._filterModel and persist
  // (they're simply not applied in grid view, which has no columns).

  const contentView = showDebugMediaCards ? (
    <DebugMediaCardGrid />
  ) : uiPreferences.libraryViewMode === 'list' ? (
    isMobile ? (
      <MobileMediaTableList />
    ) : (
      <LibraryContentMediaTable />
    )
  ) : (
    <LibraryContentMediaList />
  );

  return (
    <MediaTableProvider>
      <AddVideosModalProvider>
        <div className={css.container}>
          {isMobile ? (
            <MobileLibraryHeader />
          ) : (
            <div className={css.header} ref={headerRef}>
              <LibraryContentHeaderLeft verbsCollapsed={verbsCollapsed} />
              <LibraryContentHeaderRight compact={rightCompact} />
            </div>
          )}
          <LibraryToolbar />
          <LibraryFolderBar />
          {/* No verb pills on mobile — deliberately. They'd cost a full control
                row above the grid, and both verbs are already one tap away in the
                library's ⋯ context drawer. The desktop pills live inline in
                `LibraryContentHeaderLeft`, beside the view picker. */}
          {isMobile ? (
            <div className={css.mobileContentWrapper}>
              <div className={css.mobileContent}>{contentView}</div>
            </div>
          ) : (
            <div className={css.content}>{contentView}</div>
          )}
          <LibraryVideoSelection />
        </div>
      </AddVideosModalProvider>
    </MediaTableProvider>
  );
});
